import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import firebase from "firebase/app";
import {
  firebaseConfig,
  createAdminProfileDocument,
} from "../../firebase/firebase.utils";
import Breadcrumb from "../common/breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { notifyWarning, notifyError } from "../../utils/notify";

class RegisterAdmin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayName: "",
      email: "",
      password: "",
      confirmPassword: "",
      status: "",
      loading: false,
      createdCredentials: null, // { name, email, password, role } shown after success
      copied: false,
    };
  }

  handleChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { displayName, email, password, confirmPassword, status } = this.state;

    if (password !== confirmPassword) {
      notifyWarning("Passwords don't match.");
      return;
    }

    this.setState({ loading: true });

    // Use a secondary Firebase app so the current super admin session is NOT affected.
    // createUserWithEmailAndPassword would otherwise sign in as the new user immediately.
    let secondaryApp;
    try {
      const appName = `reg-${Date.now()}`;
      secondaryApp = firebase.apps.find((a) => a.name === appName)
        || firebase.initializeApp(firebaseConfig, appName);

      const secondaryAuth = secondaryApp.auth();
      const { user } = await secondaryAuth.createUserWithEmailAndPassword(email, password);
      await createAdminProfileDocument(user, { name: displayName, status });
      await secondaryAuth.signOut();
      await secondaryApp.delete();

      this.setState({
        displayName: "",
        email: "",
        password: "",
        confirmPassword: "",
        status: "",
        loading: false,
        createdCredentials: { name: displayName, email, password, role: status },
        copied: false,
      });
      toast.success(`Account created for ${displayName}`);
    } catch (error) {
      if (secondaryApp) {
        try { await secondaryApp.delete(); } catch (_) {}
      }
      notifyError(error.message);
      this.setState({ loading: false });
    }
  };

  handleCopy = () => {
    const { createdCredentials } = this.state;
    const text = `Name: ${createdCredentials.name}\nEmail: ${createdCredentials.email}\nPassword: ${createdCredentials.password}\nRole: ${createdCredentials.role}`;
    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2500);
    });
  };

  render() {
    const { displayName, email, password, confirmPassword, status, loading, createdCredentials, copied } = this.state;

    return (
      <div>
        <Breadcrumb title="Register Admin" parent="Admins" />
        <div className="container-fluid">
          <div className="row" style={{ justifyContent: "center" }}>
            <div className="col-sm-12 col-md-6 col-lg-5">

              {/* ── Credentials box shown after successful registration ── */}
              {createdCredentials && (
                <div
                  style={{
                    background: "#F0FDF4",
                    border: "1.5px solid #86EFAC",
                    borderRadius: 14,
                    padding: "20px 24px",
                    marginTop: 20,
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 18 }}>✅</span>
                      <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, color: "#15803D" }}>
                        Account created successfully
                      </span>
                    </div>
                    <button
                      onClick={this.handleCopy}
                      style={{
                        background: copied ? "#16A34A" : "linear-gradient(135deg, #4F46E5, #7C3AED)",
                        border: "none",
                        color: "#fff",
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 600,
                        fontSize: 12,
                        padding: "6px 14px",
                        borderRadius: 8,
                        cursor: "pointer",
                        transition: "background 0.2s ease",
                      }}
                    >
                      {copied ? "Copied!" : "Copy credentials"}
                    </button>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #BBF7D0", padding: "14px 16px" }}>
                    <CredRow label="Name" value={createdCredentials.name} />
                    <CredRow label="Email" value={createdCredentials.email} />
                    <CredRow label="Password" value={createdCredentials.password} secret />
                    <CredRow label="Role" value={createdCredentials.role} last />
                  </div>

                  <p style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#16A34A", margin: "12px 0 0", fontStyle: "italic" }}>
                    Share these credentials with the new admin. This box will disappear once you register another account.
                  </p>
                </div>
              )}

              {/* ── Registration form ── */}
              <div
                className="card"
                style={{
                  border: "none",
                  borderRadius: 16,
                  boxShadow: "0 4px 24px rgba(79,70,229,0.1)",
                  marginTop: createdCredentials ? 0 : 20,
                }}
              >
                <div
                  className="card-header"
                  style={{
                    background: "linear-gradient(135deg, #312E81 0%, #4F46E5 55%, #7C3AED 100%)",
                    borderRadius: "16px 16px 0 0",
                    padding: "20px 24px",
                  }}
                >
                  <h5 style={{ color: "#fff", margin: 0, fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                    {createdCredentials ? "Register Another Admin" : "Create New Admin Account"}
                  </h5>
                  <p style={{ color: "rgba(255,255,255,0.7)", margin: "4px 0 0", fontSize: 13, fontFamily: "Inter, sans-serif" }}>
                    The current super admin session stays active throughout.
                  </p>
                </div>

                <div className="card-body" style={{ padding: "28px 28px" }}>
                  <form onSubmit={this.handleSubmit}>
                    <div className="form-group" style={{ marginBottom: 18 }}>
                      <label style={labelStyle}>Full Name</label>
                      <input
                        className="form-control"
                        style={inputStyle}
                        name="displayName"
                        value={displayName}
                        onChange={this.handleChange}
                        type="text"
                        placeholder="John Doe"
                        required
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 18 }}>
                      <label style={labelStyle}>Email Address</label>
                      <input
                        className="form-control"
                        style={inputStyle}
                        name="email"
                        value={email}
                        onChange={this.handleChange}
                        type="email"
                        placeholder="admin@zapai.com.bd"
                        required
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 18 }}>
                      <label style={labelStyle}>Role</label>
                      <select
                        className="form-control"
                        style={inputStyle}
                        name="status"
                        value={status}
                        onChange={this.handleChange}
                        required
                      >
                        <option value="">Select role…</option>
                        <option value="Admin">Admin (Super)</option>
                        <option value="Accounts">Accounts</option>
                        <option value="Employee">Employee</option>
                        <option value="Officer">Officer</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: 18 }}>
                      <label style={labelStyle}>Password</label>
                      <input
                        className="form-control"
                        style={inputStyle}
                        name="password"
                        value={password}
                        onChange={this.handleChange}
                        type="password"
                        placeholder="••••••••"
                        required
                        minLength={6}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 24 }}>
                      <label style={labelStyle}>Confirm Password</label>
                      <input
                        className="form-control"
                        style={inputStyle}
                        name="confirmPassword"
                        value={confirmPassword}
                        onChange={this.handleChange}
                        type="password"
                        placeholder="••••••••"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: "100%",
                        background: loading ? "#C4B5FD" : "linear-gradient(135deg, #4F46E5, #7C3AED)",
                        border: "none",
                        color: "#fff",
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 600,
                        fontSize: 14,
                        padding: "12px",
                        borderRadius: 10,
                        cursor: loading ? "not-allowed" : "pointer",
                        boxShadow: "0 2px 10px rgba(109,40,217,0.3)",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {loading ? "Creating account…" : "Create Account"}
                    </button>
                  </form>
                </div>
              </div>

            </div>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }
}

function CredRow({ label, value, secret, last }) {
  const [show, setShow] = React.useState(false);
  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
      borderBottom: last ? "none" : "1px solid #DCFCE7",
    }}>
      <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 500, color: "#1E293B" }}>
          {secret && !show ? "••••••••" : value}
        </span>
        {secret && (
          <button
            type="button"
            onClick={() => setShow(!show)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "#6D28D9", fontWeight: 600, fontFamily: "Inter, sans-serif", padding: 0 }}
          >
            {show ? "hide" : "show"}
          </button>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  fontFamily: "Inter, sans-serif",
  fontSize: 12,
  fontWeight: 600,
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: 6,
  display: "block",
};

const inputStyle = {
  border: "1.5px solid #E2E8F0",
  borderRadius: 10,
  fontSize: 13,
  fontFamily: "Inter, sans-serif",
  color: "#1E293B",
  padding: "10px 14px",
  background: "#F8FAFC",
};

const mapStateToProps = (state) => ({
  currentAdmin: state.admins.currentAdmin,
});

export default connect(mapStateToProps)(withRouter(RegisterAdmin));
